// M3.5 — Import Excel modal với OTP L2 admin (PRD R-P0-B4, R-P0-B5)
// - Chọn file .xlsx → nhập TOTP 6 số → submit
// - Hiện số câu imported + danh sách row bị skip (nếu có)
// - Max 10 MB (backend enforce)

import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { boLabel } from '../lib/bo.js';
import TotpInput from './TotpInput.jsx';

export default function ImportExcelDialog({ open, bo, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [totp, setTotp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { imported, skipped: [] }

  if (!open) return null;

  function reset() {
    setFile(null);
    setTotp('');
    setSubmitting(false);
    setResult(null);
  }

  function handleClose() {
    if (submitting) return;
    reset();
    onClose();
  }

  async function doSubmit(codeOverride) {
    const code = (codeOverride ?? totp).trim();
    if (!file) return toast.error('Chọn file .xlsx');
    if (!/^\d{6}$/.test(code)) return toast.error('Mã TOTP phải là 6 chữ số');
    if (file.size > 10 * 1024 * 1024) return toast.error('File > 10 MB — backend từ chối');
    if (submitting) return;

    setSubmitting(true);
    try {
      const res = await api.questionsImport(bo, file, code);
      setResult(res);
      toast.success(`Đã import ${res.imported} câu vào ${boLabel(bo)}`);
      onDone?.(); // cho caller reload list + counts
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Import thất bại');
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    doSubmit();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-5 py-3 border-b flex items-center">
          <h3 className="font-semibold">Import Excel vào {boLabel(bo)}</h3>
          <button onClick={handleClose} className="ml-auto text-slate-400 hover:text-slate-800">✕</button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
              <div className="font-medium text-slate-800 mb-1">Định dạng yêu cầu</div>
              <div>File <code>.xlsx</code>, sheet đầu tiên, header hàng 1 gồm 3 cột:</div>
              <ul className="list-disc ml-5 mt-1 text-slate-700">
                <li><b>Chủ đề</b> (tuỳ chọn, có thể bỏ trống)</li>
                <li><b>Câu hỏi</b> (bắt buộc, ≤ 5000 ký tự)</li>
                <li><b>Đáp án</b> (bắt buộc, ≤ 20000 ký tự)</li>
              </ul>
              <div className="mt-1 text-slate-500">Các hàng trùng (Chủ đề, Câu hỏi) với file hoặc với câu đã có trong bộ sẽ bị skip.</div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">File Excel</label>
              <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm border rounded px-2 py-1.5" required />
              {file && (
                <div className="text-xs text-slate-500 mt-1">
                  {file.name} — {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Mã TOTP của bạn (6 số)</label>
              <div className="w-40">
                <TotpInput
                  value={totp}
                  onChange={setTotp}
                  onSubmit={(code) => doSubmit(code)}
                  disabled={submitting || !file}
                  placeholder="123456"
                  testId="import-totp-input"
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">Tự gửi khi đủ 6 số · Lấy từ Authenticator app (L1 admin)</div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button type="button" onClick={handleClose} disabled={submitting}
                      className="px-4 py-2 text-sm border rounded hover:bg-slate-50">
                Huỷ
              </button>
              <button type="submit" disabled={submitting || !file}
                      className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                {submitting ? 'Đang import…' : 'Import'}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 space-y-3">
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-emerald-800 font-medium">Đã import {result.imported} câu</div>
              <div className="text-xs text-emerald-700 mt-1">Bộ: {boLabel(result.bo)}</div>
            </div>

            {result.skipped?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3">
                <div className="text-amber-800 font-medium mb-2">
                  {result.skipped.length} hàng bị skip:
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {result.skipped.slice(0, 50).map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="font-mono text-amber-700">row {s.row}:</span>
                      <span className="text-amber-800">{s.reason}</span>
                    </div>
                  ))}
                  {result.skipped.length > 50 && (
                    <div className="text-amber-700 italic">… và {result.skipped.length - 50} hàng nữa</div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { reset(); }}
                      className="px-4 py-2 text-sm border rounded hover:bg-slate-50">
                Import file khác
              </button>
              <button onClick={handleClose}
                      className="px-4 py-2 text-sm bg-slate-800 text-white rounded hover:bg-slate-900">
                Xong
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
